<template>
  <v-container class="chc-client py-7">
    <template v-if="screen === 'hub'">
      <v-breadcrumbs class="app-breadcrumb pa-0 mb-2" divider="›" :items="hubBreadcrumbs" />
      <div class="chc-client__header">
        <div>
          <h1>Cadastrar CHC</h1>
          <p>Cadastre Certidões de Habilitação de Crédito individualmente ou por planilha.</p>
        </div>
        <div class="chc-client__actions">
          <v-btn color="primary" elevation="0" to="/certidoes-habilitacao/importar">Importar em lote</v-btn>
          <v-btn outlined color="primary" @click="openHistory">Ver histórico</v-btn>
        </div>
      </div>

      <div class="chc-client__journeys">
        <button type="button" @click="$router.push('/certidoes-habilitacao/nova')">
          <span class="chc-client__journey-icon">1</span>
          <h2>Cadastro individual</h2>
          <p>Cadastre uma certidão por vez, selecionando os credores relacionados logo no início.</p>
          <ul>
            <li><v-icon small color="success">mdi-check-circle</v-icon>Localize e confirme os credores relacionados</li>
            <li><v-icon small color="success">mdi-check-circle</v-icon>Informe processo, créditos, honorários e documentos</li>
            <li><v-icon small color="success">mdi-check-circle</v-icon>Confira todos os dados antes de concluir</li>
          </ul>
          <strong>Cadastrar uma CHC <v-icon small color="primary">mdi-arrow-right</v-icon></strong>
        </button>
        <button type="button" @click="$router.push('/certidoes-habilitacao/importar')">
          <span class="chc-client__journey-icon"><v-icon color="primary">mdi-table-arrow-up</v-icon></span>
          <h2>Importação em lote</h2>
          <p>Mesmo padrão da importação de comprovantes: modelo, validação, conferência e processamento por item.</p>
          <ul>
            <li><v-icon small color="success">mdi-check-circle</v-icon>Baixe o modelo com abas relacionadas por referência</li>
            <li><v-icon small color="success">mdi-check-circle</v-icon>Documentos por link compartilhado do Google Drive</li>
            <li><v-icon small color="success">mdi-check-circle</v-icon>Resultado parcial com reenvio só dos itens com falha</li>
          </ul>
          <strong>Importar planilha <v-icon small color="primary">mdi-arrow-right</v-icon></strong>
        </button>
      </div>

      <v-card outlined class="chc-client__recent">
        <v-card-title>
          <span>Últimas importações em lote</span>
          <v-spacer />
          <v-btn text color="primary" to="/certidoes-habilitacao/importar">Ver importações</v-btn>
        </v-card-title>
        <v-card-text v-if="recentImports.length" class="pa-0">
          <div v-for="item in recentImports" :key="item.id" class="chc-client__recent-row">
            <v-icon color="primary">mdi-file-excel-outline</v-icon>
            <div>
              <strong>{{ item.source_file_name }}</strong>
              <small>{{ importProgress(item) }} · {{ item.created_at | defaultDate("DD/MM/YYYY HH:mm") }}</small>
            </div>
            <v-chip x-small :color="importStatus(item.status).background"
              :text-color="importStatus(item.status).color">{{ importStatus(item.status).label }}</v-chip>
          </div>
        </v-card-text>
        <v-card-text v-else class="text-center py-8 chc-client__muted">
          Nenhuma importação em lote encontrada.
        </v-card-text>
      </v-card>
    </template>

    <template v-else>
      <v-breadcrumbs class="app-breadcrumb pa-0 mb-2" divider="›" :items="historyBreadcrumbs" />
      <div class="chc-client__header">
        <div>
          <h1>Histórico de cadastros</h1>
          <p>Consulte as certidões cadastradas, com origem, resultado, autor e data.</p>
        </div>
        <v-btn color="primary" elevation="0" to="/certidoes-habilitacao/nova">Novo cadastro</v-btn>
      </div>

      <v-card outlined class="chc-client__history">
        <div class="chc-client__filters">
          <v-text-field v-model="filters.term" dense outlined hide-details clearable
            prepend-inner-icon="mdi-magnify" label="Buscar por credor, CPF/CNPJ ou processo" />
          <v-select v-model="filters.origin" :items="originOptions" item-text="text" item-value="value"
            dense outlined hide-details label="Origem: todas" />
          <v-select v-model="filters.result" :items="resultOptions" item-text="text" item-value="value"
            dense outlined hide-details label="Resultado: todos" />
        </div>
        <v-data-table :headers="headers" :items="filteredRecords" :loading="loading"
          :items-per-page="10" class="app-table chc-client__table"
          no-data-text="Nenhuma certidão cadastrada">
          <template v-slot:[`item.creditor`]="{ item }">
            <div v-for="creditor in item.creditors" :key="creditor.registration">
              <strong>{{ creditor.name }}</strong>
              <small>{{ formatRegistration(creditor.registration) }}</small>
            </div>
          </template>
          <template v-slot:[`item.process`]="{ item }">
            <strong>{{ item.origin_process }}</strong>
          </template>
          <template v-slot:[`item.submission_origin`]="{ item }">
            <v-chip x-small color="#E8ECFB" text-color="#2A3070">
              {{ item.submission_origin === "debtor_batch" ? "Em lote" : "Individual" }}
            </v-chip>
          </template>
          <template v-slot:[`item.status`]="{ item }">
            <v-chip x-small :color="status(item.status).background" :text-color="status(item.status).color">
              <i class="chc-client__dot" :style="{ background: status(item.status).dot }" />
              {{ status(item.status).label }}
            </v-chip>
            <small v-if="status(item.status).reason" class="error--text">{{ status(item.status).reason }}</small>
          </template>
          <template v-slot:[`item.operation`]="{ item }">
            <strong>{{ item.created_at | defaultDate("DD/MM/YYYY HH:mm") }}</strong>
            <small>{{ item.submitter_name || "Equipe da devedora" }}</small>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn x-small outlined color="primary" @click="open(item)">
              {{ [4, 6].includes(Number(item.status)) ? "Atualizar" : "Consultar" }}
            </v-btn>
          </template>
        </v-data-table>
      </v-card>
    </template>

    <v-dialog v-model="detailsOpen" max-width="880">
      <v-card v-if="details">
        <v-card-title>
          Detalhes do cadastro
          <v-spacer /><v-btn icon @click="detailsOpen = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" sm="6"><strong>Processo</strong><div>{{ details.origin_process }}</div></v-col>
            <v-col cols="12" sm="6"><strong>Órgão julgador</strong><div>{{ details.judging_body }}</div></v-col>
            <v-col cols="12"><strong>Credores</strong><div v-for="creditor in details.creditors" :key="creditor.id">{{ creditor.name }} — {{ formatRegistration(creditor.registration) }}</div></v-col>
          </v-row>
          <v-divider class="my-4" />
          <div class="d-flex flex-wrap" style="gap:8px">
            <v-btn v-if="details.chc_file_url" small outlined color="primary" :href="details.chc_file_url" target="_blank"><v-icon left>mdi-download</v-icon>CHC</v-btn>
            <v-btn v-if="details.homologation_decision_file_url" small outlined color="primary" :href="details.homologation_decision_file_url" target="_blank">Decisão</v-btn>
            <v-btn v-if="details.calculation_spreadsheet_file_url" small outlined color="primary" :href="details.calculation_spreadsheet_file_url" target="_blank">Planilha</v-btn>
          </div>
          <v-alert v-if="[4, 6].includes(Number(details.status))" color="#FFF7E8" border="left" colored-border class="mt-5">
            <strong>{{ Number(details.status) === 6 ? "A CHC precisa ser atualizada." : "A Administração Judicial solicitou novos documentos." }}</strong>
            <div class="mt-3">
              <v-file-input v-if="isDocumentRequested(1)" v-model="updatedChc" outlined dense prepend-icon=""
                prepend-inner-icon="mdi-paperclip" label="Nova Certidão de Habilitação de Crédito" />
              <v-file-input v-if="isDocumentRequested(2)" v-model="updatedConfidential" outlined dense prepend-icon=""
                prepend-inner-icon="mdi-lock-outline" label="Novo processo integral sigiloso" />
              <v-btn color="primary" elevation="0" :loading="updatingDocuments"
                :disabled="!updatedChc && !updatedConfidential" @click="updateDocuments">Enviar documentos atualizados</v-btn>
            </div>
          </v-alert>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  name: "CreditCertificateList",
  data: () => ({
    screen: "hub",
    hubBreadcrumbs: [{ text: "Início", disabled: true }, { text: "Certidão de Habilitação de Crédito", disabled: true }],
    historyBreadcrumbs: [
      { text: "Início", disabled: true },
      { text: "Certidão de Habilitação de Crédito", disabled: false },
      { text: "Histórico", disabled: true },
    ],
    loading: false,
    records: [],
    imports: [],
    filters: { term: "", origin: "", result: "" },
    originOptions: [{ text: "Todas", value: "" }, { text: "Individual", value: "debtor_individual" }, { text: "Em lote", value: "debtor_batch" }],
    resultOptions: [
      { text: "Todos", value: "" }, { text: "Disponibilizada", value: "available" },
      { text: "Aguardando documentos", value: "pending_docs" }, { text: "Concluída", value: "completed" },
    ],
    details: null,
    detailsOpen: false,
    updatedChc: null,
    updatedConfidential: null,
    updatingDocuments: false,
    headers: [
      { text: "Credor relacionado", value: "creditor" },
      { text: "Processo", value: "process" },
      { text: "Origem", value: "submission_origin" },
      { text: "Resultado", value: "status" },
      { text: "Operação", value: "operation" },
      { text: "", value: "actions", align: "end", sortable: false },
    ],
  }),
  computed: {
    recentImports() { return this.imports.slice(0, 3); },
    filteredRecords() {
      const term = String(this.filters.term || "").toLowerCase().replace(/[./-]/g, "");
      return this.records.filter((item) => {
        if (this.filters.origin && item.submission_origin !== this.filters.origin) return false;
        const statusGroup = [4, 6].includes(Number(item.status)) ? "pending_docs"
          : [1, 2].includes(Number(item.status)) ? "completed" : "available";
        if (this.filters.result && statusGroup !== this.filters.result) return false;
        if (!term) return true;
        const haystack = [
          item.origin_process,
          ...(item.creditors || []).flatMap((creditor) => [creditor.name, creditor.registration]),
        ].join(" ").toLowerCase().replace(/[./-]/g, "");
        return haystack.includes(term);
      });
    },
  },
  created() {
    this.screen = this.$route.query.view === "historico" ? "history" : "hub";
    this.load();
    this.loadImports();
  },
  watch: {
    "$route.query.view"(value) { this.screen = value === "historico" ? "history" : "hub"; },
  },
  methods: {
    openHistory() { this.$router.push({ path: "/certidoes-habilitacao", query: { view: "historico" } }); },
    async load() {
      this.loading = true;
      try {
        const response = await axios.get("/api/client/credit-certificate-requests", { params: { page: 1, limit: 50 } });
        this.records = (response.data.content || {}).data || [];
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right" });
      } finally { this.loading = false; }
    },
    async loadImports() {
      try {
        const response = await axios.get("/api/client/credit-certificate-imports", { params: { page: 1, limit: 3 } });
        this.imports = (response.data.content || {}).data || [];
      } catch (error) {
        this.imports = [];
      }
    },
    async open(item) {
      try {
        const response = await axios.get(`/api/client/credit-certificate-requests/${item.id}`);
        this.details = response.data.content;
        this.updatedChc = null;
        this.updatedConfidential = null;
        this.detailsOpen = true;
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right" });
      }
    },
    async updateDocuments() {
      this.updatingDocuments = true;
      try {
        const form = new FormData();
        if (this.updatedChc) form.append("chc_file", this.updatedChc);
        if (this.updatedConfidential) form.append("confidential_full_process_file", this.updatedConfidential);
        await axios.patch(`/api/client/credit-certificate-requests/${this.details.id}/update-documents`, form);
        this.$dialog.notify.success("Documentos atualizados com sucesso.", { position: "top-right" });
        this.detailsOpen = false;
        await this.load();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right" });
      } finally { this.updatingDocuments = false; }
    },
    isDocumentRequested(type) {
      if (!this.details) return false;
      if (Number(this.details.status) === 6) return Number(type) === 1;
      let requested = this.details.requested_documents;
      if (typeof requested === "string") {
        try { requested = JSON.parse(requested); } catch (error) { requested = null; }
      }
      return !!(requested && requested.files &&
        requested.files.some((file) => Number(file.type) === Number(type) && Number(file.status) === 0));
    },
    status(value) {
      return {
        0: { label: "Disponibilizada", background: "#E6F4EA", color: "#1E6B34", dot: "#1E6B34" },
        1: { label: "Aprovada", background: "#E6F4EA", color: "#1E6B34", dot: "#1E6B34" },
        2: { label: "Rejeitada", background: "#FDECEC", color: "#A32020", dot: "#A32020" },
        3: { label: "Processando", background: "#E8ECFB", color: "#2A3070", dot: "#2A3070" },
        4: { label: "Documentos solicitados", background: "#FEF7EC", color: "#8A4B08", dot: "#C77A17" },
        5: { label: "Documentos atualizados", background: "#E8F4F6", color: "#176B75", dot: "#238A96" },
        6: { label: "Certidão desatualizada", background: "#F3EFFB", color: "#5B3A9E", dot: "#7E55AB" },
      }[Number(value)] || { label: "Disponibilizada", background: "#E6F4EA", color: "#1E6B34", dot: "#1E6B34" };
    },
    importStatus(value) {
      return {
        completed: { label: "Concluída", background: "#E6F4EA", color: "#1E6B34" },
        completed_with_errors: { label: "Com falhas", background: "#FEF7EC", color: "#8A4B08" },
        processing: { label: "Processando", background: "#E8ECFB", color: "#2A3070" },
        awaiting_confirmation: { label: "Aguardando conferência", background: "#F2F4F7", color: "#475467" },
      }[value] || { label: "Em validação", background: "#F2F4F7", color: "#475467" };
    },
    importProgress(item) {
      return `${Number(item.processed_items || 0)} de ${Number(item.total_items || 0)}`;
    },
    formatRegistration(value) {
      const digits = String(value || "").replace(/\D/g, "");
      if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      return value || "-";
    },
  },
};
</script>

<style scoped>
.chc-client { max-width:1440px; }
.chc-client__header { display:flex; align-items:flex-end; justify-content:space-between; gap:24px; margin-bottom:22px; }
.chc-client__header h1 { color:#101828; font-size:30px; line-height:1.2; }
.chc-client__header p,.chc-client__muted { margin:6px 0 0; color:#667085; }
.chc-client__actions { display:flex; gap:10px; }
.chc-client__journeys { display:grid; grid-template-columns:1fr 1fr; gap:18px; }
.chc-client__journeys > button { padding:26px; border:1px solid #E4E7EC; border-radius:10px; background:#fff; text-align:left; transition:.16s; }
.chc-client__journeys > button:hover { transform:translateY(-2px); border-color:#2A3070; box-shadow:0 8px 24px rgba(42,48,112,.09); }
.chc-client__journey-icon { display:flex; width:48px; height:48px; align-items:center; justify-content:center; border-radius:50%; background:#EEF1FB; color:#2A3070; font-size:20px; font-weight:700; }
.chc-client__journeys h2 { margin-top:18px; color:#101828; font-size:20px; }
.chc-client__journeys p { min-height:48px; margin-top:7px; color:#667085; }
.chc-client__journeys ul { min-height:98px; margin:18px 0; padding:0; list-style:none; }
.chc-client__journeys li { display:flex; align-items:flex-start; gap:8px; margin-top:8px; color:#475467; }
.chc-client__journeys strong { color:#2A3070; }
.chc-client__recent,.chc-client__history { margin-top:20px; overflow:hidden; border-color:#E4E7EC !important; border-radius:10px; }
.chc-client__recent-row { display:grid; grid-template-columns:auto 1fr auto; align-items:center; gap:14px; padding:15px 20px; border-top:1px solid #E4E7EC; }
.chc-client__recent-row strong,.chc-client__recent-row small,.chc-client__table strong,.chc-client__table small { display:block; }
.chc-client__recent-row small,.chc-client__table small { margin-top:3px; color:#667085; }
.chc-client__filters { display:grid; grid-template-columns:minmax(320px,1fr) 180px 180px; gap:10px; padding:14px; }
.chc-client__table { border-top:1px solid #E4E7EC; }
.chc-client__table strong { color:#344054; font-size:13px; }
.chc-client__table [data-v-slot="process"] strong { font-family:monospace; }
.chc-client__dot { display:inline-block; width:7px; height:7px; margin-right:6px; border-radius:50%; }
@media (max-width:900px) {
  .chc-client__header { align-items:flex-start; flex-direction:column; }
  .chc-client__journeys { grid-template-columns:1fr; }
  .chc-client__filters { grid-template-columns:1fr; }
}
@media (max-width:600px) {
  .chc-client__actions { width:100%; flex-direction:column; }
}
</style>
